import { displayToast } from "../../../components/app/AppToast";
import { AnalyticsItemList } from "../../../tools/analytics/analytics.types";
import { gaEventContentShare } from "../../../tools/analytics/domains/content.analytics";
import {
  convertVideoToItemData,
  gaEventVideoShare,
} from "../../../tools/analytics/domains/video.analytics";
import { resolveVideoPublisherDisplay } from "../../content/videoPublisher";
import { useOnboardingGuard } from "./useOnboardingGuard";
import { useTranslation } from "react-i18next";
import type { CurrentUser } from "./useCurrentUser";
import { apiClient, type Api } from "../../../services/api";
import { shareUrl } from "../../app/utils/shareUrl";
import { REDIRECT_HOST } from "../../../config";

type VideoListItem = Extract<
  Api["get"]["/webapp/public/content"]["output"]["body"]["docs"][number],
  { content_format: "Video" }
>;
type ShareVideoData = VideoListItem;

export type UseShareVideo = {
  /**
   * `roomId` is the room the video is shared from, or null outside a room. A
   * share made in a room keeps the room, so the reader lands in it and meets
   * its Join offer (`docs/shared-content-access/README.md`).
   */
  triggerShare: (
    video: ShareVideoData,
    user: CurrentUser,
    roomId: string | null
  ) => Promise<void>;
};

function publisherName(video: ShareVideoData): string | null {
  const resolved = resolveVideoPublisherDisplay(video).name;
  if (resolved === null) return null;
  if (resolved === "") return null;
  return resolved;
}

export function useShareVideo(
  analyticsListName: AnalyticsItemList
): UseShareVideo {
  const { t } = useTranslation();
  const onboardingGuard = useOnboardingGuard();

  const triggerShare = async (
    video: ShareVideoData,
    currentUser: CurrentUser,
    roomId: string | null
  ): Promise<void> => {
    try {
      if (
        !onboardingGuard(
          t("🔒 Please complete your profile first to share this content. ")
        )
      ) {
        return;
      }

      const isStory = video.videoFormat === "story";
      const section = isStory ? "video/story" : "video";
      const path =
        roomId === null
          ? `/${section}/${video.slug}`
          : `/room/${section}/${roomId}/${video.slug}`;
      const longUrl = `${globalThis.location.origin}${path}?shared=${currentUser.id}`;
      // Not awaited: `shareUrl` must start inside the click (see there).
      const link = apiClient
        .post("/webapp/private/content/:id/shares", {
          param: { id: video.id },
        })
        .then(() =>
          apiClient
            .post("/webapp/private/share-links", {
              json: {
                type: isStory ? "video-story" : "video",
                slug: video.slug,
                roomId,
              },
            })
            .then(({ body }) => `https://${REDIRECT_HOST}/${body}`)
            .catch(() => longUrl)
        );
      const resolvedPublisher = publisherName(video);
      const text =
        resolvedPublisher === null
          ? t(
              "I've just watched this video you may be interested in: {{title}}.",
              {
                title: video.title,
              }
            )
          : t(
              "I've just watched this video you may be interested in: {{title}} by {{publisherName}}.",
              {
                title: video.title,
                publisherName: resolvedPublisher,
              }
            );

      await shareUrl(video.title, text, link);

      gaEventVideoShare({
        video,
        listName: analyticsListName,
      });
      gaEventContentShare(
        convertVideoToItemData({
          video,
          listName: analyticsListName,
        })
      );
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  };

  return { triggerShare };
}
